import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { bountyService } from '../api/services';
import { useSettings } from '../context/SettingsContext';
import { usePermissions } from '../context/PermissionsContext';
import { useBountyCount } from '../context/BountyCountContext';
import { BOUNTY_TYPES, STATUSES, SORTS, LEVEL_GROUP_ORDER, typeLabel, levelLabel, statusLabel, statusClass, levelClass } from '../utils/bountyLabels';
import './Bounty.css';

// localStorage 鍵：檢視模式（網格/列表）與各折疊群組的收合狀態，記憶使用者偏好
const VIEW_KEY = 'tech-forum:bounty-view';
const COLLAPSE_KEY = 'tech-forum:bounty-collapsed';
// 折疊模式一次抓取的上限（client 端分組，免分頁）；超過時提示僅顯示前 N 筆
const GROUP_FETCH_LIMIT = 100;

// 取本機今日日期字串（yyyy-mm-dd），供逾期判斷，避免 UTC 時區誤差
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// created_at 可能帶時間，僅取日期部分顯示
const fmtDate = (s) => (s ? String(s).slice(0, 10) : '—');

// 懸賞榜頁面：mine=false 為全部懸賞（含報修/任務分類）、mine=true 為「我領取的」個人領取介面。
const BountyBoardPage = ({ mine = false }) => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { hasPermission } = usePermissions();
  const { totalCount, mineCount } = useBountyCount();
  const pointsEnabled = settings.bounty_points_enabled === 'true';
  const canCreate = hasPermission('bounty.create');

  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('new');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // 檢視模式：grid（網格）/ list（列表），記憶於 localStorage
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(VIEW_KEY) || 'grid');
  // 各群組收合狀態（key -> true 表示收合）
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || '{}'); } catch { return {}; }
  });

  // 是否啟用折疊分組：「我領取的」與「任務」分頁皆平鋪不分組；「全部」與「報修」分頁才分組
  const grouped = !mine && (type === '' || type === 'REPAIR');

  // 篩選條件改變時回到第一頁
  useEffect(() => { setPage(1); }, [type, status, sort, mine]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    // 折疊模式一次抓較多筆於 client 端分組；平鋪模式維持原本分頁
    const params = { sort, page: grouped ? 1 : page, limit: grouped ? GROUP_FETCH_LIMIT : 20 };
    if (!mine && type) params.type = type;
    if (status) params.status = status;
    const fetcher = mine ? bountyService.mine : bountyService.list;
    fetcher(params)
      .then((res) => {
        if (cancelled) return;
        setItems(res.data?.items || []);
        setPagination(res.data?.pagination || { page: 1, pages: 1, total: 0 });
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mine, type, status, sort, page, grouped]);

  const changeView = (m) => {
    setViewMode(m);
    try { localStorage.setItem(VIEW_KEY, m); } catch { /* 略過 */ }
  };

  const toggleGroup = (key) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); } catch { /* 略過 */ }
      return next;
    });
  };

  // 逾期：有截止日且已過今日、且尚未結案/取消
  const isOverdue = (b) => b.deadline && b.deadline < todayStr() && b.status !== 'CLOSED' && b.status !== 'CANCELLED';

  // 依分頁情境將 items 切成有序群組：任務一組 + 報修依等級（L1>高>中>低）；空組自動隱藏
  const buildGroups = (list) => {
    if (type === 'REPAIR') {
      return LEVEL_GROUP_ORDER
        .map((lv) => ({ key: `REPAIR_${lv}`, label: levelLabel(lv), items: list.filter((b) => b.level === lv) }))
        .filter((g) => g.items.length > 0);
    }
    // 全部分頁：任務群組在前，再接報修各等級群組
    const groups = [];
    const tasks = list.filter((b) => b.bounty_type === 'TASK');
    if (tasks.length) groups.push({ key: 'TASK', label: '任務', items: tasks });
    LEVEL_GROUP_ORDER.forEach((lv) => {
      const repairItems = list.filter((b) => b.bounty_type === 'REPAIR' && b.level === lv);
      if (repairItems.length) groups.push({ key: `REPAIR_${lv}`, label: `報修・${levelLabel(lv)}`, items: repairItems });
    });
    return groups;
  };

  // 網格卡片
  const renderCard = (b) => (
    <Link key={b.id} to={`/bounties/${b.id}`} className={`bounty-card ${isOverdue(b) ? 'overdue' : ''}`}>
      <div className="bounty-card-top">
        <span className={`badge badge-type-${String(b.bounty_type).toLowerCase()}`}>{typeLabel(b.bounty_type)}</span>
        <span className={`badge ${levelClass(b.level)}`}>等級 {levelLabel(b.level)}</span>
        <span className={`badge ${statusClass(b.status)}`}>{statusLabel(b.status)}</span>
      </div>
      <h3 className="bounty-card-title">{b.title}</h3>
      <div className="bounty-card-meta">
        <span title="開立時間">開立 {fmtDate(b.created_at)}</span>
        <span title="結束時間" className={isOverdue(b) ? 'overdue-text' : ''}>結束 {b.deadline || '未定'}</span>
        <span title="預估工時">⏱ {b.estimated_hours != null ? `${b.estimated_hours} 小時` : '—'}</span>
        {pointsEnabled && <span title="點數">⭐ {b.points} 點</span>}
      </div>
      <div className="bounty-card-foot">
        <span className="bounty-card-owner">
          {b.owner_name ? `負責：${b.owner_name}` : '尚無人領取'}
          {b.collaborator_count > 0 && `（+${b.collaborator_count} 協作）`}
        </span>
        <span className="bounty-card-author">由 {b.author_name} 開立</span>
      </div>
    </Link>
  );

  // 列表列（精簡橫向，強調兩個時間）
  const renderRow = (b) => (
    <Link key={b.id} to={`/bounties/${b.id}`} className={`bounty-row ${isOverdue(b) ? 'overdue' : ''}`}>
      <div className="bounty-row-main">
        <div className="bounty-row-badges">
          <span className={`badge badge-type-${String(b.bounty_type).toLowerCase()}`}>{typeLabel(b.bounty_type)}</span>
          <span className={`badge ${levelClass(b.level)}`}>{levelLabel(b.level)}</span>
          <span className={`badge ${statusClass(b.status)}`}>{statusLabel(b.status)}</span>
        </div>
        <span className="bounty-row-title">{b.title}</span>
      </div>
      <div className="bounty-row-times">
        <span title="開立時間">開立 {fmtDate(b.created_at)}</span>
        <span title="結束時間" className={isOverdue(b) ? 'overdue-text' : ''}>結束 {b.deadline || '未定'}</span>
      </div>
      <div className="bounty-row-side">
        <span className="bounty-row-owner">{b.owner_name ? `負責：${b.owner_name}` : '尚無人領取'}</span>
        {pointsEnabled && <span className="bounty-row-points">⭐ {b.points}</span>}
      </div>
    </Link>
  );

  // 依檢視模式渲染一批項目
  const renderItems = (list) => (
    viewMode === 'list'
      ? <div className="bounty-list">{list.map(renderRow)}</div>
      : <div className="bounty-grid">{list.map(renderCard)}</div>
  );

  // 折疊分組渲染
  const renderGroups = () => {
    const groups = buildGroups(items);
    if (!groups.length) return <div className="bounty-empty">目前沒有符合條件的懸賞</div>;
    return (
      <div className="bounty-groups">
        {groups.map((g) => {
          const isCollapsed = !!collapsed[g.key];
          return (
            <section key={g.key} className="bounty-group">
              <button type="button" className="bounty-group-header" onClick={() => toggleGroup(g.key)} aria-expanded={!isCollapsed}>
                <span className={`bounty-group-caret ${isCollapsed ? 'collapsed' : ''}`}>▾</span>
                <span className="bounty-group-label">{g.label}</span>
                <span className="bounty-group-count">{g.items.length}</span>
              </button>
              {!isCollapsed && renderItems(g.items)}
            </section>
          );
        })}
        {pagination.total > items.length && (
          <div className="bounty-truncate-note">案件較多，目前僅顯示前 {items.length} 筆（共 {pagination.total} 筆），請用上方篩選縮小範圍。</div>
        )}
      </div>
    );
  };

  return (
    <div className="bounty-board">
      <div className="container">
        <div className="bounty-board-header">
          <h1>{mine ? '我領取的懸賞' : '懸賞榜'}</h1>
          <div className="bounty-board-header-actions">
            <Link to="/bounties" className={`btn btn-sm ${!mine ? 'btn-primary' : 'btn-secondary'}`}>
              懸賞榜{totalCount > 0 && <span className="board-tab-count">{totalCount > 99 ? '99+' : totalCount}</span>}
            </Link>
            <Link to="/bounties/mine" className={`btn btn-sm ${mine ? 'btn-primary' : 'btn-secondary'}`}>
              我領取的{mineCount > 0 && <span className="board-tab-count">{mineCount > 99 ? '99+' : mineCount}</span>}
            </Link>
            {canCreate && (
              <button type="button" className="btn btn-accent" onClick={() => navigate('/bounties/create')}>＋ 開立懸賞</button>
            )}
          </div>
        </div>

        {/* 報修/任務分類僅在「全部」模式顯示；我領取的不分類 */}
        {!mine && (
          <div className="bounty-tabs">
            <button className={`bounty-tab ${type === '' ? 'active' : ''}`} onClick={() => setType('')}>全部</button>
            {BOUNTY_TYPES.map((t) => (
              <button key={t.value} className={`bounty-tab ${type === t.value ? 'active' : ''}`} onClick={() => setType(t.value)}>{t.label}</button>
            ))}
          </div>
        )}

        <div className="bounty-filters">
          <label>
            狀態
            <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">全部</option>
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
          <label>
            排序
            <select className="form-control" value={sort} onChange={(e) => setSort(e.target.value)}>
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
          <span className="bounty-total">共 {pagination.total} 筆</span>
          {/* 檢視模式切換：網格 / 列表 */}
          <div className="bounty-view-toggle">
            <button
              type="button"
              className={`bounty-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => changeView('grid')}
              title="網格檢視"
              aria-label="網格檢視"
            >▦ 網格</button>
            <button
              type="button"
              className={`bounty-view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => changeView('list')}
              title="列表檢視"
              aria-label="列表檢視"
            >☰ 列表</button>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {loading ? (
          <div className="bounty-loading">載入中...</div>
        ) : items.length === 0 ? (
          <div className="bounty-empty">{mine ? '你目前沒有領取中的懸賞' : '目前沒有符合條件的懸賞'}</div>
        ) : grouped ? (
          renderGroups()
        ) : (
          renderItems(items)
        )}

        {/* 平鋪模式才分頁；折疊分組為一次載入 */}
        {!grouped && pagination.pages > 1 && (
          <div className="bounty-pagination">
            <button className="btn btn-sm btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>上一頁</button>
            <span>第 {pagination.page} / {pagination.pages} 頁</span>
            <button className="btn btn-sm btn-secondary" disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)}>下一頁</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BountyBoardPage;
